import { IPromise } from 'angular';
import { ICommand } from 'core-ts-types';
import {
  IInitPollQuestionModel,
  IPollQuestionFlags,
  IPollQuestionModel,
  IStudentAnswerObject,
  IStudentAnswerObjectFromChild
} from '../../poll_interfaces';
import { PollQueryModel } from '../../queries/poll_query_model';
import { PollResultsQueryModel } from '../../queries/poll_results_query_model';
import { PollPlatformServant } from '../../servants/poll_platform_servant';
import { IBuiltPoll } from '../../servants/poll_results_servant';
import { IQuestionTypesEnum } from '../../types/question_types';

import * as _4 from 'lodash4';
import { ScheduledPollQuery } from '../../queries/scheduled_poll_query';

export class PollContainterController {
  private models: IPollQuestionModel | IInitPollQuestionModel;
  private flags: IPollQuestionFlags;
  private poll: IBuiltPoll;
  private isComplete: boolean;
  private studentAnswers: IStudentAnswerObject;
  private validationCallbackParent: (responseObjectFromChild: IStudentAnswerObjectFromChild) => void;
  private afterComplete: () => void;
  private markComplete: () => void;
  private $onChanges: (arg: any) => void;

  constructor(
    private ngPollQueryModel: PollQueryModel,
    private QuestionTypesEnum: IQuestionTypesEnum,
    private updatePollStatusCommand: ICommand,
    private updatePollAnswerCommand: ICommand,
    private ngPollPlatformServant: PollPlatformServant,
    private ngPollResultsQueryModel: PollResultsQueryModel,
    private loadPathPollCommand: ICommand,
    private loadPollSummaryCommand: ICommand,
    private ngScheduledPollQuery: ScheduledPollQuery
  ) {
    this._setToPollDefaults();
  }

  $onInit(): void {
    this.validationCallbackParent = (responseObjectFromChild: IStudentAnswerObjectFromChild): void => {
      this.flags.isValid = responseObjectFromChild.isValid;
      this._constructAnswerObject(responseObjectFromChild);
    };

    this.$onChanges = (changes: ng.IChangesObject): void => {
      this._refresh();
      if (this.showResultsOrResources()) {
        this.ngScheduledPollQuery.completeCurrentScheduledPoll();
      }
    };
  }

  onSubmitAnswer(): IPromise<any> | void {
    this.ngPollPlatformServant.scroll();
    this.flags.showValidationAlert = false;
    if (this.flags.showResults) {
      this._setToPollDefaults();
      return this._updatePoll().then(() => this.afterComplete());
    }
    if (this.flags.isValid && !this.flags.isSubmitting) {
      this.flags.isSubmitting = true;
      return this.updatePollAnswerCommand(this.models.activeQuestionResponse).then((next_question_id: number): void => {
        if (_4.isNil(next_question_id)) {
          this._completePollOrShowResults();
        } else {
          this._continuePoll(next_question_id);
        }
        this.flags.isSubmitting = false;
      });
    } else {
      this.flags.showValidationAlert = true;
    }
  }

  isEmpty(): boolean {
    return _4.isEmpty(this.models.activeQuestion) && !this.flags.showResults;
  }

  showResultsOrResources(): boolean {
    return this.flags.showResults || this.ngPollResultsQueryModel.resultsPageFlags.hasResources;
  }

  hasActiveQuestion(): boolean {
    return !_4.isEmpty(this.models.activeQuestion);
  }

  onKeyPressSubmitAnswer(event: JQueryEventObject) {
    if (event.key === 'Enter') {
      this.onSubmitAnswer();
    }
  }

  private _refresh(): void {
    if (this.ngPollQueryModel.isUpdatedPoll) {
      this.models.activeQuestionId = this.ngPollQueryModel.result.first_question_id;
      this.ngPollQueryModel.isUpdatedPoll = false;
    }

    if (!this.models.activeQuestionId) {
      this.models.activeQuestionId = this.ngPollQueryModel.result.first_question_id;
    }

    if (this.isComplete) {
      this.models.activeQuestionId = '';
    }

    this.models = {
      activeQuestionId: this.models.activeQuestionId,
      activeQuestion: this._getActiveQuestion(this.models.activeQuestionId, this.ngPollQueryModel.result.question),
      activeQuestionResponse: ''
    } as IPollQuestionModel;

    const question_type = _4.result(this.models.activeQuestion, 'question_type', '');

    this.flags = {
      isFreeTextQuestion: this.QuestionTypesEnum.isFreeText(question_type),
      isChoiceQuestion: this.QuestionTypesEnum.isChoice(question_type),
      isMultipleQuestion: this.QuestionTypesEnum.isMultiple(question_type),
      isValid: this.flags.isValid || this.isComplete || this.poll.is_completed,
      showResults: this._shouldShowResults()
    } as IPollQuestionFlags;
  }

  private _constructAnswerObject(responseObjectFromChild: IStudentAnswerObjectFromChild): void {
    this.models.activeQuestionResponse = {
      pollId: this.poll.poll.id,
      questionID: this.models.activeQuestion.id,
      response: responseObjectFromChild.selectedResponse
    };
  }

  private _getActiveQuestion(currentQuestionId: number, questions: object): object {
    return _.result(questions, currentQuestionId);
  }

  private _completePollOrShowResults(): void {
    this.poll.is_completed = true;
    this.ngPollResultsQueryModel.run();
    this.flags.showResults = this._shouldShowResults();

    if (!this.flags.showResults) {
      this._updatePoll().then(() => this.afterComplete());
      this._setToPollDefaults();
    } else {
      this.markComplete();
      this.loadPollSummaryCommand(this.poll.poll).then(() => {
        if (this._showResultsAndIsDesktop()) {
          this._updatePoll();
        } else {
          this._refresh();
        }
      });
    }
  }

  private _continuePoll(next_question_id: number): void {
    this.models.activeQuestionId = next_question_id;
    this.flags.isValid = false;
    this._refresh();
  }

  private _showResultsAndIsDesktop(): boolean {
    return this.poll.poll.show_results_page && this.ngPollPlatformServant.environment === 'desktop';
  }

  private _updatePoll(): any {
    return this.updatePollStatusCommand(this.poll.poll);
  }

  private _setToPollDefaults(): void {
    this.models = {
      activeQuestionId: '',
      activeQuestion: '',
      activeQuestionResponse: ''
    } as IInitPollQuestionModel;

    this.flags = {
      isFreeTextQuestion: false,
      isChoiceQuestion: false,
      isMultipleQuestion: false,
      isValid: false,
      showResults: false,
      isSubmitting: false,
      showValidationAlert: false
    };
  }

  private _shouldShowResults(): boolean {
    if (!!this.poll.is_completed || this.isComplete) {
      return (
        this.ngPollResultsQueryModel.resultsPageFlags.hasResources ||
        this.ngPollResultsQueryModel.resultsPageFlags.showResults
      );
    }
    return false;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-poll/src/components/poll_container/poll_container_controller.ts